import { inject, Injectable } from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { ColDef } from 'ag-grid-community/dist/lib/entities/colDef';
import { Observable } from 'rxjs';

import {
  GridService,
  FormlyHideExpression,
  FormlyProps,
  FormlyExpressionProperties,
  IdDescription,
  FormlyService,
  ApplicationSettings,
} from 'tms-lib';
import { Customer, Product, Supplier } from '../../models';
import { CustomerService } from '../edit-create/sales/customer.service';
import { ProductService } from '../edit-create/production/product.service';
import { SupplierShipperService } from '../edit-create/purchasing/supplier-shipper.service';
import { SupplierService } from '../edit-create/purchasing/supplier.service';

@Injectable({
  providedIn: 'root',
})
export class TmsFormlyService extends FormlyService {
  protected readonly gridService = inject(GridService);
  protected readonly customerService = inject(CustomerService);
  protected readonly supplierService = inject(SupplierService);
  protected readonly supplierShipperService = inject(SupplierShipperService);
  protected readonly productService = inject(ProductService);

  readonly customerSupplierGridColumnDefs: ColDef[] = [
    this.gridService.text('key', $localize`Key`),
    this.gridService.text('name', $localize`Name`),
    this.gridService.text('line1', $localize`Line 1`),
    this.gridService.text('line2', $localize`Line 2`),
    this.gridService.text('line3', $localize`Line 3`),
    this.gridService.text('line4', $localize`Line 4`),
    this.gridService.text('townCity', $localize`Town/City`),
    this.gridService.text('region', $localize`Region`),
    this.gridService.text('postCode', $localize`Postcode`),
    this.gridService.text('countryName', $localize`Country`),
    this.gridService.text('tel', $localize`Telephone`),
    this.gridService.text('fax', $localize`Fax`),
    this.gridService.text('mobileNo', $localize`Mobile`),
    this.gridService.text('email', $localize`Email`),
    this.gridService.text('web', $localize`Website`),
    this.gridService.text('vatRegNo', $localize`VAT Number`),
  ];

  readonly productGridColumnDefs: ColDef[] = [
    this.gridService.text('key', $localize`Key`),
    this.gridService.text('shortDescription', $localize`Short Description`),
    this.gridService.text('description', $localize`Description`),
    this.gridService.text('oldCode', $localize`Alternate Code`),
    this.gridService.text('partNumber', $localize`Part No`),
    this.gridService.text('productType', $localize`Product Type`),
    this.gridService.text('clearanceProduct', $localize`Clearance Product`),
  ];

  warpWeftPlan(
    key: string,
    label?: string,
    // model is the hosting form's model (WarpPlanEdit, WeftPlanEdit, plan headers...)
    editable?: (model: any, row: number) => boolean,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
  ): FormlyFieldConfig[] {
    return [
      {
        key,
        type: 'warp-weft-plan',
        props: {
          label,
          editable,
          ...props,
        },
        expressions: {
          hide: hideExpression,
          ...expressionProperties,
        },
      },
      { key: key + 'Repeats' },
      { key: 'code' },
    ];
  }

  addressLookup(
    key: string,
    countries: Observable<IdDescription[]>,
    label?: string,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
  ): FormlyFieldConfig {
    return {
      key,
      type: 'address-lookup',
      props: {
        label: label ?? $localize`Find Address`,
        countries,
        ...props,
      },
      expressions: {
        hide: hideExpression,
        ...expressionProperties,
      },
    };
  }

  productSearch(
    key: string,
    validationKey: string,
    selected: (model: Product) => void,
    clear: () => void,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    id?: (model: any) => any,
    id2?: (model: any) => any,
  ): FormlyFieldConfig[] {
    const modalSearch = this.modalSearch(
      key,
      $localize`Find Product`,
      this.productService,
      this.productGridColumnDefs,
      selected,
      clear,
      { ...props, blankSearchSetting: ApplicationSettings.allowBlankProductSearch },
      hideExpression,
      id,
      id2,
      undefined,
    );

    return props?.required
      ? [modalSearch, this.requiredFieldValidation(validationKey, $localize`Product`)]
      : [modalSearch];
  }

  customerSearch(
    key: string,
    validationKey: string,
    label: string,
    selected: (model: Customer) => void,
    clear: () => void,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
  ): FormlyFieldConfig[] {
    const modalSearch = this.modalSearch(
      key,
      label ?? $localize`Find Customer`,
      this.customerService,
      this.customerSupplierGridColumnDefs,
      selected,
      clear,
      props,
      hideExpression,
    );

    return props?.required
      ? [modalSearch, this.requiredFieldValidation(validationKey, $localize`Customer`)]
      : [modalSearch];
  }

  supplierSearch(
    key: string,
    validationKey: string,
    label: string,
    selected: (model: Supplier) => void,
    clear: () => void,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
  ): FormlyFieldConfig[] {
    const modalSearch = this.modalSearch(
      key,
      label ?? $localize`Find Supplier`,
      this.supplierService,
      this.customerSupplierGridColumnDefs,
      selected,
      clear,
      props,
      hideExpression,
    );

    return props?.required
      ? [modalSearch, this.requiredFieldValidation(validationKey, $localize`Supplier`)]
      : [modalSearch];
  }

  supplierShipperSearch(
    key: string,
    validationKey: string,
    label: string,
    sellingCompanyId: (model: any) => string,
    warehouseId: (model: any) => string,
    selected: (model: Supplier) => void,
    clear: () => void,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
  ): FormlyFieldConfig[] {
    const modalSearch = this.modalSearch(
      key,
      label ?? $localize`Find Shipper`,
      this.supplierShipperService,
      this.customerSupplierGridColumnDefs,
      selected,
      clear,
      props,
      hideExpression,
      sellingCompanyId,
      warehouseId,
    );

    return props?.required
      ? [modalSearch, this.requiredFieldValidation(validationKey, $localize`Shipper`)]
      : [modalSearch];
  }
}
